import React, { useEffect, useState, useCallback } from 'react';
import '../super-admin.css';
import { 
  Typography, Card, List, Tag, Button, Input, Select, message, Row, Col, 
  Statistic, Collapse, Tooltip, Modal, Form, Space
} from 'antd';
import { 
  UserOutlined, GlobalOutlined, CodeOutlined, EyeOutlined, EditOutlined,
  DeleteOutlined, PlusOutlined, ReloadOutlined, FilterOutlined, 
  ThunderboltOutlined, CopyOutlined
} from '@ant-design/icons';
import { API_BASE as apiBase, authHeaders } from '../../config/api';

const { Title } = Typography;
const { Panel } = Collapse;
const { TextArea } = Input;

// Snippet type configurations
const snippetTypeConfig = {
  analytics: { name: 'Analytics', color: '#3730a3', icon: <EyeOutlined /> },
  tracking: { name: 'Tracking', color: '#059669', icon: <CodeOutlined /> },
  marketing: { name: 'Marketing', color: '#dc2626', icon: <GlobalOutlined /> },
  custom: { name: 'Custom', color: '#d97706', icon: <CodeOutlined /> },
  seo: { name: 'SEO', color: '#7c3aed', icon: <GlobalOutlined /> }
};

const statusColors = {
  active: 'green',
  inactive: 'red',
  draft: 'orange'
};

const AllSnippets = () => {
  const [websites, setWebsites] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSnippet, setEditingSnippet] = useState(null);
  const [generatorVisible, setGeneratorVisible] = useState(false);
  const [generatedCode, setGeneratedCode] = useState('');
  const [usersList, setUsersList] = useState([]);
  const [userWebsites, setUserWebsites] = useState([]);
  const [form] = Form.useForm();
  const [generatorForm] = Form.useForm();

  // Load websites with snippets
  const loadWebsiteSnippets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: '50',
        offset: '0'
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('snippet_type', typeFilter);
      
      const res = await fetch(`${apiBase}/admin/website-snippets?${params}`, {
        headers: { ...authHeaders() }
      });
      
      if (!res.ok) throw new Error('Failed to load website snippets');
      const data = await res.json();
      
      setWebsites(data.websites || []);
    } catch (err) {
      message.error(err.message || 'Error loading website snippets');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, typeFilter]);
  // Load snippet statistics
  const loadStats = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/admin/website-snippets/stats`, {
        headers: { ...authHeaders() }
      });
      
      if (!res.ok) throw new Error('Failed to load snippet statistics');
      const data = await res.json();
      
      setStats(data);
    } catch (err) {
      console.error('Error loading snippet statistics:', err);
    }
  }, []);

  // Load users list for generator dropdown
  const loadUsersList = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/admin/users-list`, {
        headers: { ...authHeaders() }
      });
      
      if (!res.ok) throw new Error('Failed to load users list');
      const data = await res.json();
      
      setUsersList(data);
    } catch (err) {
      console.error('Error loading users list:', err);
    }
  }, []);

  // Delete snippet
  const deleteSnippet = async (snippetId) => {
    try {
      const res = await fetch(`${apiBase}/admin/website-snippets/${snippetId}`, {
        method: 'DELETE',
        headers: { ...authHeaders() }
      });
      
      if (!res.ok) throw new Error('Failed to delete snippet');
      
      message.success('Snippet deleted successfully');
      loadWebsiteSnippets();
      loadStats();
    } catch (err) {
      message.error(err.message || 'Error deleting snippet');
    }
  };

  // Save snippet (create or update)
  const saveSnippet = async (values) => {
    try {
      const method = editingSnippet ? 'PUT' : 'POST';
      const url = editingSnippet 
        ? `${apiBase}/admin/website-snippets/${editingSnippet.id}`
        : `${apiBase}/admin/website-snippets`;
      
      const res = await fetch(url, {
        method,
        headers: { 
          ...authHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(values)
      });
      
      if (!res.ok) throw new Error(`Failed to ${editingSnippet ? 'update' : 'create'} snippet`);
      
      message.success(`Snippet ${editingSnippet ? 'updated' : 'created'} successfully`);
      setModalVisible(false);
      setEditingSnippet(null);
      form.resetFields();
      loadWebsiteSnippets();
      loadStats();
    } catch (err) {
      message.error(err.message || `Error ${editingSnippet ? 'updating' : 'creating'} snippet`);
    }
  };

  useEffect(() => {
    loadWebsiteSnippets();
    loadStats();
  }, [loadWebsiteSnippets, loadStats]);

  const handleRefresh = () => {
    loadWebsiteSnippets();
    loadStats();
  };

  const openEditModal = (snippet) => {
    setEditingSnippet(snippet);
    form.setFieldsValue(snippet);
    setModalVisible(true);
  };

  const openCreateModal = () => {
    setEditingSnippet(null);
    form.resetFields();
    setModalVisible(true);
    loadUsersList(); // Load users for the dropdown
  };

  const openGeneratorModal = () => {
    generatorForm.resetFields();
    setGeneratedCode('');
    setGeneratorVisible(true);
    loadUsersList(); // Load users when modal opens
  };

  // Load websites for selected user (from admin clients-websites)
  const loadWebsitesForUser = useCallback(async (userId) => {
    try {
      const params = new URLSearchParams({ limit: '50', offset: '0' });
      const res = await fetch(`${apiBase}/admin/clients-websites?${params}`, {
        headers: { ...authHeaders() }
      });
      if (!res.ok) throw new Error('Failed to load user websites');
      const data = await res.json();
      const clients = Array.isArray(data.clients) ? data.clients : [];
      const client = clients.find(c => String(c.user_id) === String(userId));
      const list = [];
      if (client) {
        if (client.business_account?.website) {
          list.push({ url: client.business_account.website, source: 'business_account' });
        }
        (client.websites || []).forEach(w => list.push({ url: w.url, source: w.source || 'websites_table' }));
      }
      setUserWebsites(list);
      // If exactly one website, prefill
      if (list.length === 1) {
        generatorForm.setFieldsValue({ website_url: list[0].url });
      }
    } catch (err) {
      console.error('Failed to load user websites', err);
      setUserWebsites([]);
    }
  }, [generatorForm]);

  // Generate tracking snippet
  const generateTrackingSnippet = async (values) => {
    try {
      const res = await fetch(`${apiBase}/admin/website-snippets/generate-tracker`, {
        method: 'POST',
        headers: { 
          ...authHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(values)
      });
      
      if (!res.ok) throw new Error('Failed to generate tracking snippet');
      const data = await res.json();
      
      setGeneratedCode(data.snippet_code);
      message.success('Tracking snippet generated successfully!');
      
      // Optionally pre-fill the main form with suggested values
      const suggested = data.suggested_snippet;
      form.setFieldsValue({
        snippet_name: suggested.snippet_name,
        snippet_type: suggested.snippet_type,
        status: suggested.status,
        placement: suggested.placement,
        priority: suggested.priority,
        website_url: values.website_url,
        user_id: values.user_id,
        snippet_code: data.snippet_code
      });
      
    } catch (err) {
      message.error(err.message || 'Error generating tracking snippet');
    }
  };

  // Copy generated code to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedCode).then(() => {
      message.success('Code copied to clipboard!');
    }).catch(() => {
      message.error('Failed to copy code');
    });
  };

  // Use generated snippet (close generator and open main form)
  const useGeneratedSnippet = () => {
    setGeneratorVisible(false);
    setModalVisible(true);
  };

  return (
    <div className="sa-content">
      <Title level={2} style={{ marginBottom: 24 }}>Website Snippets Management</Title>

      {/* Statistics Overview */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6} lg={6} xl={6}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="Total Snippets"
              value={stats.totals?.total_snippets || 0}
              prefix={<CodeOutlined style={{ color: '#3730a3' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={6} xl={6}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="Active Snippets"
              value={stats.by_status?.active || 0}
              prefix={<EyeOutlined style={{ color: '#059669' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={6} xl={6}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="Total Websites"
              value={stats.totals?.total_websites || 0}
              prefix={<GlobalOutlined style={{ color: '#d97706' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={6} xl={6}>
          <Card hoverable style={{ borderRadius: 14 }}>
            <Statistic
              title="Total Users"
              value={stats.totals?.total_users || 0}
              prefix={<UserOutlined style={{ color: '#7c3aed' }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content Card */}
      <Card 
        hoverable 
        style={{ borderRadius: 14 }} 
        bodyStyle={{ padding: 20 }}
        loading={loading}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ 
              width: 44, 
              height: 44, 
              borderRadius: 10, 
              background: '#3730a322', 
              color: '#3730a3', 
              display: 'grid', 
              placeItems: 'center', 
              fontSize: 20 
            }}>
              <CodeOutlined />
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 600, color: '#1f2937' }}>Website Snippets</div>
              <div style={{ color: '#64748b', fontSize: 13 }}>Manage code snippets for user websites</div>
            </div>
          </div>
          <Space>
            <Button 
              type="default" 
              icon={<ThunderboltOutlined />}
              onClick={openGeneratorModal}
              style={{ 
                background: '#059669', 
                borderColor: '#059669', 
                color: 'white' 
              }}
            >
              Generate Tracker
            </Button>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={openCreateModal}
            >
              Add Snippet
            </Button>
            <Button 
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        </div>

        {/* Filters */}
        <div style={{ 
          display: 'flex', 
          gap: 12, 
          flexWrap: 'wrap',
          padding: '12px 16px', 
          background: '#f8fafc', 
          borderRadius: 8, 
          marginBottom: 16 
        }}>
          <Input
            placeholder="Search snippets, websites, users..."
            prefix={<FilterOutlined />}
            style={{ minWidth: 250, flex: 1 }}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onPressEnter={loadWebsiteSnippets}
          />
          <Select
            placeholder="Filter by status"
            style={{ minWidth: 120 }}
            value={statusFilter}
            onChange={setStatusFilter}
            allowClear
          >
            <Select.Option value="active">Active</Select.Option>
            <Select.Option value="inactive">Inactive</Select.Option>
            <Select.Option value="draft">Draft</Select.Option>
          </Select>
          <Select
            placeholder="Filter by type"
            style={{ minWidth: 140 }}
            value={typeFilter}
            onChange={setTypeFilter}
            allowClear
          >
            {Object.entries(snippetTypeConfig).map(([key, config]) => (
              <Select.Option key={key} value={key}>{config.name}</Select.Option>
            ))}
          </Select>
        </div>

        {/* Websites with Snippets */}
        <div style={{ 
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          overflow: 'hidden'
        }}>
          {websites.length === 0 && !loading ? (
            <div style={{ 
              padding: 32, 
              textAlign: 'center',
              color: '#64748b' 
            }}>
              <CodeOutlined style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }} />
              <div>No website snippets found</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>
                Code snippets will appear here when added to user websites
              </div>
            </div>
          ) : (
            <Collapse 
              ghost 
              expandIconPosition="end"
              style={{ background: 'white' }}
            >
              {websites.map((website, index) => (
                <Panel
                  key={website.user_id}
                  header={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0' }}>
                      <div style={{ 
                        width: 40, 
                        height: 40, 
                        borderRadius: 10, 
                        background: '#3730a3', 
                        color: 'white', 
                        display: 'grid', 
                        placeItems: 'center', 
                        fontSize: 16,
                        fontWeight: 600
                      }}>
                        {website.user_name ? website.user_name.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ fontSize: 16, fontWeight: 600, color: '#1f2937' }}>
                            {website.user_name || 'Unknown User'}
                          </span>
                          {website.company_name && (
                            <Tag color="blue" style={{ margin: 0 }}>
                              {website.company_name}
                            </Tag>
                          )}
                          <Tag color="purple" style={{ margin: 0 }}>
                            {website.snippet_count} snippets
                          </Tag>
                        </div>
                        <div style={{ color: '#64748b', fontSize: 13 }}>
                          {website.user_email}
                          {website.business_website && (
                            <span> • {website.business_website}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  }
                  style={{ 
                    borderBottom: index < websites.length - 1 ? '1px solid #f1f5f9' : 'none',
                    margin: 0
                  }}
                >
                  <div style={{ paddingLeft: 52 }}>
                    {/* Website Info */}
                    {website.business_website && (
                      <div style={{ 
                        background: '#f8fafc', 
                        padding: 16, 
                        borderRadius: 8, 
                        marginBottom: 16 
                      }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>
                          <GlobalOutlined style={{ marginRight: 8 }} />
                          Website Information
                        </div>
                        <div style={{ fontSize: 14, color: '#1f2937' }}>
                          <a 
                            href={website.business_website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: '#2563eb', textDecoration: 'none' }}
                          >
                            {website.business_website}
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Snippets */}
                    {website.snippets.length === 0 ? (
                      <div style={{ 
                        padding: 16, 
                        textAlign: 'center', 
                        color: '#64748b',
                        background: '#f9fafb',
                        borderRadius: 8
                      }}>
                        <CodeOutlined style={{ fontSize: 24, marginBottom: 8, opacity: 0.3 }} />
                        <div>No snippets added to this website</div>
                      </div>
                    ) : (
                      <List
                        size="small"
                        dataSource={website.snippets}
                        renderItem={(snippet) => {
                          const typeConfig = snippetTypeConfig[snippet.snippet_type] || {
                            name: snippet.snippet_type,
                            color: '#6b7280',
                            icon: <CodeOutlined />
                          };

                          return (
                            <List.Item style={{ 
                              padding: '12px 16px',
                              background: '#ffffff',
                              border: '1px solid #e5e7eb',
                              borderRadius: 6,
                              marginBottom: 8
                            }}>
                              <div style={{ width: '100%' }}>
                                {/* Snippet Header */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                      <div style={{ color: typeConfig.color, fontSize: 16 }}>
                                        {typeConfig.icon}
                                      </div>
                                      <span style={{ fontSize: 14, fontWeight: 600, color: '#1f2937' }}>
                                        {snippet.snippet_name}
                                      </span>
                                      <Tag color={typeConfig.color} size="small" style={{ margin: 0 }}>
                                        {typeConfig.name}
                                      </Tag>
                                      <Tag color={statusColors[snippet.status]} size="small" style={{ margin: 0 }}>
                                        {snippet.status}
                                      </Tag>
                                    </div>
                                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                                      Placement: {snippet.placement} • Priority: {snippet.priority} • 
                                      Updated: {new Date(snippet.updated_at).toLocaleDateString()}
                                    </div>
                                  </div>
                                  <Space>
                                    <Tooltip title="Edit Snippet">
                                      <Button 
                                        type="text" 
                                        size="small"
                                        icon={<EditOutlined />}
                                        onClick={() => openEditModal(snippet)}
                                      />
                                    </Tooltip>
                                    <Tooltip title="Delete Snippet">
                                      <Button 
                                        type="text" 
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => deleteSnippet(snippet.id)}
                                      />
                                    </Tooltip>
                                  </Space>
                                </div>

                                {/* Snippet Code Preview */}
                                <div style={{ 
                                  background: '#f8fafc', 
                                  padding: 8, 
                                  borderRadius: 4, 
                                  fontSize: 12,
                                  fontFamily: 'monospace',
                                  maxHeight: 100,
                                  overflow: 'auto'
                                }}>
                                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                    {snippet.snippet_code.substring(0, 200)}
                                    {snippet.snippet_code.length > 200 && '...'}
                                  </pre>
                                </div>
                              </div>
                            </List.Item>
                          );
                        }}
                      />
                    )}
                  </div>
                </Panel>
              ))}
            </Collapse>
          )}
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingSnippet ? 'Edit Snippet' : 'Create New Snippet'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingSnippet(null);
          form.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={saveSnippet}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="snippet_name"
                label="Snippet Name"
                rules={[{ required: true, message: 'Please enter snippet name' }]}
              >
                <Input placeholder="e.g., Google Analytics" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="snippet_type"
                label="Snippet Type"
                rules={[{ required: true, message: 'Please select snippet type' }]}
              >
                <Select placeholder="Select type">
                  {Object.entries(snippetTypeConfig).map(([key, config]) => (
                    <Select.Option key={key} value={key}>{config.name}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select placeholder="Select status">
                  <Select.Option value="active">Active</Select.Option>
                  <Select.Option value="inactive">Inactive</Select.Option>
                  <Select.Option value="draft">Draft</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="placement"
                label="Placement"
                rules={[{ required: true, message: 'Please select placement' }]}
              >
                <Select placeholder="Select placement">
                  <Select.Option value="head">Head</Select.Option>
                  <Select.Option value="body">Body</Select.Option>
                  <Select.Option value="footer">Footer</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="website_url"
                label="Website URL"
                rules={[{ required: true, message: 'Please enter website URL' }]}
              >
                <Input placeholder="https://example.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="user_id"
                label="Select User"
                rules={[{ required: true, message: 'Please select a user' }]}
              >
                <Select 
                  placeholder="Select user"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {usersList.map(user => (
                    <Select.Option key={user.id} value={user.id}>
                      <div>
                        <div style={{ fontWeight: 500 }}>{user.name}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>
                          {user.email}
                          {user.company_name && ` • ${user.company_name}`}
                        </div>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="priority"
                label="Priority"
                initialValue={0}
              >
                <Input type="number" placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={12}>
              {/* Empty column for spacing */}
            </Col>
          </Row>

          <Form.Item
            name="snippet_code"
            label="Snippet Code"
            rules={[{ required: true, message: 'Please enter snippet code' }]}
          >
            <TextArea 
              rows={8} 
              placeholder="Enter your HTML/JavaScript code here..."
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingSnippet ? 'Update' : 'Create'} Snippet
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Tracking Snippet Generator Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ThunderboltOutlined style={{ color: '#059669' }} />
            <span>Generate Website Activity Tracker</span>
          </div>
        }
        open={generatorVisible}
        onCancel={() => {
          setGeneratorVisible(false);
          setGeneratedCode('');
          generatorForm.resetFields();
        }}
        footer={null}
        width={900}
      >
        <div style={{ marginBottom: 16 }}>
          <p style={{ color: '#64748b', margin: 0 }}>
            Generate a comprehensive tracking snippet to monitor website activities including page views, 
            clicks, form submissions, scroll depth, and more.
          </p>
        </div>

        <Form
          form={generatorForm}
          layout="vertical"
          onFinish={generateTrackingSnippet}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="website_url"
                label="Website URL"
                rules={[
                  { required: true, message: 'Please enter website URL' },
                  { type: 'url', message: 'Please enter a valid URL' }
                ]}
              >
                <Input placeholder="https://example.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="user_id"
                label="Select User"
                rules={[{ required: true, message: 'Please select a user' }]}
              >
                <Select 
                  placeholder="Select user"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  onChange={(val) => {
                    loadWebsitesForUser(val);
                  }}
                >
                  {usersList.map(user => (
                    <Select.Option key={user.id} value={user.id}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: 500 }}>{user.name}</div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>
                            {user.email}
                            {user.company_name && ` • ${user.company_name}`}
                          </div>
                        </div>
                        {user.website && (
                          <div style={{ fontSize: 11, color: '#059669' }}>
                            {user.website}
                          </div>
                        )}
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* If user has websites, allow picking to auto-fill URL */}
          {userWebsites.length > 0 && (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label="Pick one of user's websites (optional)">
                  <Select
                    placeholder="Select a website to auto-fill URL"
                    onChange={(url) => generatorForm.setFieldsValue({ website_url: url })}
                  >
                    {userWebsites.map((w, idx) => (
                      <Select.Option key={`${w.url}-${idx}`} value={w.url}>
                        {w.url} {w.source === 'business_account' ? '(Business Account)' : ''}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          )}

          <div style={{ marginBottom: 16 }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#1f2937' }}>Tracking Options</h4>
            <Row gutter={[16, 8]}>
              <Col span={8}>
                <Form.Item name={['tracking_options', 'trackPageViews']} valuePropName="checked" initialValue={true}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" />
                    <span>Page Views</span>
                  </label>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name={['tracking_options', 'trackClicks']} valuePropName="checked" initialValue={true}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" />
                    <span>Click Tracking</span>
                  </label>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name={['tracking_options', 'trackScrollDepth']} valuePropName="checked" initialValue={true}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" />
                    <span>Scroll Depth</span>
                  </label>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name={['tracking_options', 'trackFormSubmissions']} valuePropName="checked" initialValue={true}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" />
                    <span>Form Submissions</span>
                  </label>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name={['tracking_options', 'trackFileDownloads']} valuePropName="checked" initialValue={true}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" />
                    <span>File Downloads</span>
                  </label>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name={['tracking_options', 'trackErrors']} valuePropName="checked" initialValue={true}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" />
                    <span>JavaScript Errors</span>
                  </label>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name={['tracking_options', 'trackUserSessions']} valuePropName="checked" initialValue={true}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" />
                    <span>Session Duration</span>
                  </label>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name={['tracking_options', 'trackDeviceInfo']} valuePropName="checked" initialValue={true}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" />
                    <span>Device Info</span>
                  </label>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name={['tracking_options', 'trackLocation']} valuePropName="checked" initialValue={false}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" />
                    <span>Location (Privacy)</span>
                  </label>
                </Form.Item>
              </Col>
            </Row>
          </div>

          <Form.Item style={{ marginBottom: 16, textAlign: 'center' }}>
            <Button type="primary" htmlType="submit" size="large" icon={<ThunderboltOutlined />}>
              Generate Tracking Code
            </Button>
          </Form.Item>
        </Form>

        {generatedCode && (
          <div style={{ marginTop: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h4 style={{ margin: 0, color: '#1f2937' }}>Generated Tracking Code</h4>
              <Space>
                <Button 
                  type="default" 
                  icon={<CopyOutlined />}
                  onClick={copyToClipboard}
                >
                  Copy Code
                </Button>
                <Button 
                  type="primary" 
                  onClick={useGeneratedSnippet}
                >
                  Use as Snippet
                </Button>
              </Space>
            </div>
            <div style={{ 
              background: '#f8fafc', 
              border: '1px solid #e5e7eb',
              borderRadius: 8, 
              padding: 16,
              maxHeight: 400,
              overflow: 'auto'
            }}>
              <pre style={{ 
                margin: 0, 
                fontFamily: 'Monaco, Consolas, monospace', 
                fontSize: 12,
                lineHeight: 1.4,
                whiteSpace: 'pre-wrap'
              }}>
                {generatedCode}
              </pre>
            </div>
            <div style={{ 
              background: '#e0f2fe', 
              border: '1px solid #b3e5fc',
              borderRadius: 6, 
              padding: 12, 
              marginTop: 12,
              fontSize: 13
            }}>
              <strong>Instructions:</strong>
              <ol style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                <li>Copy the generated code above</li>
                <li>Paste it into the &lt;head&gt; section of your website</li>
                <li>The tracker will automatically start collecting data</li>
                <li>View the collected data in the "Website Activity Tracking" page</li>
              </ol>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AllSnippets;
